import { useState } from 'react'
import { CircleDot, Loader2 } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { toast } from 'sonner'

import { InquiryStageEnum, type QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { pipelineLeadStageIconClass } from '@/components/pipeline/pipeline-job-view'
import { jobLeadDisplayStage } from '@/components/pipeline/job-detail-view'
import { MarkLeadLostDialog } from '@/components/pipeline/mark-lead-lost-dialog'
import { cn } from '@/lib/utils'
import { useInquiry } from '@/stores/leads.store'
import { useMoveLeadStage } from '@/stores/pipeline.store'
import { ICON_SWAP_TRANSITION, usePendingWithMinimumVisibleDuration } from '@/hooks/use-pending-with-minimum-visible-duration'

interface PipelineLeadStageControlProps {
  inquiryId: string
  className?: string
  /**
   * The job's quotes' statuses, undefined while they load. With none sent, a
   * stored Quote Sent stage reads as In Progress (see `jobLeadDisplayStage`).
   */
  quoteStatuses?: QuoteStatusEnum[]
}

const OPEN_LEAD_STAGES = [
  { stage: InquiryStageEnum.INQUIRY_STAGE_NEW, label: 'New' },
  { stage: InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS, label: 'In Progress' },
] as const

// Stale/Rejected end the lead: the job's status becomes Stale/Lost and the
// default Open filter drops it from the Pipeline board and its counts.
const ENDED_LEAD_STAGES = [
  { stage: InquiryStageEnum.INQUIRY_STAGE_STALE, label: 'Stale' },
  { stage: InquiryStageEnum.INQUIRY_STAGE_REJECTED, label: 'Rejected' },
] as const

const STAGE_LABELS: Record<InquiryStageEnum, string> = {
  [InquiryStageEnum.INQUIRY_STAGE_UNKNOWN]: 'Unknown',
  [InquiryStageEnum.INQUIRY_STAGE_NEW]: 'New',
  [InquiryStageEnum.INQUIRY_STAGE_IN_PROGRESS]: 'In Progress',
  [InquiryStageEnum.INQUIRY_STAGE_QUOTE_SENT]: 'Quote Sent',
  [InquiryStageEnum.INQUIRY_STAGE_ACCEPTED]: 'Accepted',
  [InquiryStageEnum.INQUIRY_STAGE_REJECTED]: 'Rejected',
  [InquiryStageEnum.INQUIRY_STAGE_STALE]: 'Stale',
  [InquiryStageEnum.INQUIRY_STAGE_REFERRED]: 'Referred',
}

const isEndedStage = (stage: InquiryStageEnum) =>
  stage === InquiryStageEnum.INQUIRY_STAGE_STALE ||
  stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED ||
  stage === InquiryStageEnum.INQUIRY_STAGE_REFERRED

// Inset from the dropdown's own left edge to where an item's *content*
// (first flex child) actually renders, all from `ui/select.tsx` and not
// ours to change: the content's 1px `border`, its Viewport's `p-1` (4px),
// and SelectItem's own `pl-2` (8px) = 13px total. Rather than hacking each
// item's own padding (which broke the item's hover/selected highlight —
// the dot rendered left of the pill, on top of the menu's border), the
// menu itself shifts left by this amount (`alignOffset`) and widens by
// twice this amount so its right edge extends the same distance the left
// edge now overhangs. That lands the *unmodified* 13px inset exactly at
// x = 0 — the same x position as the trigger's `left-0` dot on its
// zero-padding row — while every item keeps its normal padding and
// highlight pill.
const STAGE_MENU_EDGE_INSET_PX = 13

/**
 * A dropdown option's label plus its stage's color dot, matching the size
 * (h-4/16px) of the trigger's own animated dot; its x position matches via
 * the menu-level shift above rather than any per-item margin hack. The
 * selected-state checkmark stays on the right (`SelectItem`'s own
 * `absolute right-2`).
 */
function StageOptionContent({ label, stage }: { label: string; stage: InquiryStageEnum }) {
  return (
    <span className="flex w-full items-center gap-2">
      <CircleDot
        data-testid="stage-option-dot"
        className={cn('h-4 w-4 shrink-0', pipelineLeadStageIconClass(stage))}
      />
      <span className="whitespace-nowrap">{label}</span>
    </span>
  )
}

/**
 * Lead stage picker for the job detail page's Lead segment: New / In
 * Progress, plus Stale / Rejected to end the lead. The Pipeline board's lead
 * columns are server-derived from the linked inquiry's stage (see
 * `job_sql.go`'s `board_stage` CASE) while the job is still in
 * `JOB_PHASE_NEW`, so callers only mount this in that phase. Any inquiry
 * stage renders — including one set outside this picker (e.g. Quote Sent,
 * which shows as In Progress once none of the job's quotes is out) — so the
 * lead can always be moved from here, except Accepted: booking is what accepts
 * a lead, so Accepted is never offered, and a lead accepted by hand before
 * that rule renders a read-only "not booked" badge pointing at booking it. Picking Rejected asks for a reason
 * first (the shared reject-reason dialog) and only moves the lead once it's
 * confirmed; the other stages move straight away.
 */
export function PipelineLeadStageControl({ inquiryId, className, quoteStatuses }: PipelineLeadStageControlProps) {
  const { inquiry } = useInquiry(inquiryId)
  const { moveLeadStage, isPending } = useMoveLeadStage()
  const showLoader = usePendingWithMinimumVisibleDuration(isPending)
  const prefersReducedMotion = useReducedMotion()
  const [isRejectOpen, setIsRejectOpen] = useState(false)

  if (!inquiry) return null

  const isAccepted = inquiry.stage === InquiryStageEnum.INQUIRY_STAGE_ACCEPTED
  // What the trigger shows and the menu checks; moves still compare against the stored stage.
  const shownStage = jobLeadDisplayStage(inquiry.stage, quoteStatuses)

  const handleSelect = async (value: string) => {
    const stage = Number(value) as InquiryStageEnum
    if (stage === inquiry.stage) return
    if (stage === InquiryStageEnum.INQUIRY_STAGE_REJECTED) {
      // The reason and notes are saved with the stage; cancelling leaves it as is.
      setIsRejectOpen(true)
      return
    }
    try {
      await moveLeadStage(inquiryId, stage)
    } catch (error) {
      console.error('Failed to move lead stage:', error)
      toast.error('Failed to move lead')
    }
  }

  return (
    // `relative` so the status dot can pin to this row's own left edge, and the
    // trigger below is `w-full` of it, so `--radix-select-trigger-width` — used to
    // size the dropdown — is this whole row's width, not just the label+chevron.
    <div className={cn('relative flex min-w-0 items-center', className)}>
      {isAccepted ? (
        // Booking accepts a lead and moves the job past `JOB_PHASE_NEW`, so an
        // Accepted lead still here was accepted by hand (before booking set
        // the stage) and never booked. Read-only: booking it is the way on.
        <div className="flex h-auto w-full min-w-0 items-center pl-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="warning" className="cursor-default">
                Accepted · not booked
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="bottom">Nothing is booked yet. Book it from the ⋯ menu: Book without a quote</TooltipContent>
          </Tooltip>
        </div>
      ) : (
        <Select value={String(shownStage)} onValueChange={handleSelect} disabled={isPending}>
          <SelectTrigger
            size="sm"
            aria-label="Lead stage"
            className={cn(
              'h-auto w-full min-w-0 gap-1 border-none bg-transparent p-0 pl-6 text-sm font-medium shadow-none hover:bg-transparent focus-visible:ring-0 dark:bg-transparent dark:hover:bg-transparent',
              isEndedStage(shownStage) && 'text-destructive'
            )}
          >
            <SelectValue>{STAGE_LABELS[shownStage]}</SelectValue>
          </SelectTrigger>
          <SelectContent
            // `position="popper"` (ui/select.tsx's default) is what makes
            // `align`/`alignOffset` apply against the trigger's rect at all —
            // the other Radix Select mode, "item-aligned", positions off the
            // selected item instead and ignores these. Widen by exactly
            // 2 × STAGE_MENU_EDGE_INSET_PX (26px) so the right edge overhangs
            // the row by the same amount the left edge now does. That width is
            // only a floor: the slim stepper's trigger can be narrower than the
            // longest label, so the menu grows to fit its labels on one line.
            align="start"
            alignOffset={-STAGE_MENU_EDGE_INSET_PX}
            className="w-max min-w-[calc(var(--radix-select-trigger-width)+26px)]"
          >
            {OPEN_LEAD_STAGES.map(({ stage, label }) => (
              <SelectItem key={stage} value={String(stage)}>
                <StageOptionContent label={label} stage={stage} />
              </SelectItem>
            ))}
            <SelectSeparator />
            {ENDED_LEAD_STAGES.map(({ stage, label }) => (
              <SelectItem key={stage} value={String(stage)} className="text-destructive focus:text-destructive">
                <StageOptionContent label={label} stage={stage} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {/* Fixed-size, non-interactive so the dot ↔ loader crossfade never shifts
          layout or steals clicks from the trigger underneath it. */}
      <span className="pointer-events-none absolute top-1/2 left-0 inline-flex h-4 w-4 shrink-0 -translate-y-1/2 items-center justify-center">
        <AnimatePresence initial={false}>
          {showLoader ? (
            <motion.span
              key="loader"
              className="absolute inset-0 flex items-center justify-center"
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.7 }}
              transition={prefersReducedMotion ? { duration: 0 } : ICON_SWAP_TRANSITION}
            >
              <Loader2
                role="status"
                aria-label="Moving lead stage"
                className="h-4 w-4 animate-spin text-muted-foreground motion-reduce:animate-none"
              />
            </motion.span>
          ) : (
            <motion.span
              key="dot"
              data-testid="lead-stage-dot"
              className="absolute inset-0 flex items-center justify-center"
              initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, scale: 0.7 }}
              transition={prefersReducedMotion ? { duration: 0 } : ICON_SWAP_TRANSITION}
            >
              <CircleDot className={cn('h-4 w-4', pipelineLeadStageIconClass(shownStage))} />
            </motion.span>
          )}
        </AnimatePresence>
      </span>
      <MarkLeadLostDialog
        open={isRejectOpen}
        onOpenChange={setIsRejectOpen}
        inquiryId={inquiryId}
        description="Pick why it was lost. The job leaves the open Pipeline and its counts; you can move it back to an open stage from here at any time."
      />
    </div>
  )
}
